import type { IntlShape, MessageDescriptor } from 'react-intl';
import { VIDEO_CREDIT_COST } from 'fym-shared/src/music24/music24Plans';
import type { ServiceExtraChipTone } from '../extras/serviceExtraTypes';
import { shownLink } from '../serviceLinkEdit';
import { studioThemeMessages } from '../../promote/studio/studioThemeMessages';
import type { YouTubeExtraAction, YouTubeExtraChip, YouTubeExtraItemView } from './youtubeExtraRules';
import { youtubeExtrasMessages as m } from './youtubeExtrasMessages';

export const YOUTUBE_ACTION_LABELS: Record<YouTubeExtraAction, MessageDescriptor> = {
  connect: m.actionConnect,
  reconnect: m.actionReconnect,
  makeIt: m.actionMakeIt,
  upload: m.actionUpload,
  replace: m.actionReplace,
  retry: m.actionRetry,
  uploadAgain: m.actionUploadAgain,
  status: m.actionStatus,
  changeTime: m.actionChangeTime,
  open: m.actionOpen,
};

/** The name of the item: the asset type, or "Full videos" for the connect item. */
export function youtubeItemTitle(intl: IntlShape, item: YouTubeExtraItemView): string {
  return item.themeId === null
    ? intl.formatMessage(m.fullVideos)
    : intl.formatMessage(studioThemeMessages[item.themeId].name);
}

/** The chip tones (design a1: `wt` violet, `by` green, `mk` grey, `bad` red). */
export function youtubeChipTone(chip: YouTubeExtraChip): ServiceExtraChipTone {
  switch (chip.kind) {
    case 'rendering':
    case 'goesUp':
    case 'uploading':
      return 'work';
    case 'onYouTube':
      return 'ok';
    case 'publicAt':
    case 'private':
    case 'notMade':
      return 'muted';
    case 'failed':
    case 'reconnect':
    case 'removed':
      return 'bad';
  }
}

export function youtubeChipText(intl: IntlShape, chip: YouTubeExtraChip): string {
  switch (chip.kind) {
    case 'rendering':
      return intl.formatMessage(m.chipRendering, { progress: Math.round(chip.progress) });
    case 'goesUp':
      return intl.formatMessage(m.chipGoesUp);
    case 'uploading':
      return intl.formatMessage(m.chipUploading);
    case 'publicAt':
      return chip.at === null
        ? intl.formatMessage(m.chipScheduled)
        : intl.formatMessage(m.chipPublicAt, {
            date: intl.formatDate(chip.at, { weekday: 'short', day: 'numeric', month: 'short' }),
          });
    case 'onYouTube':
      return intl.formatMessage(m.chipOnYouTube);
    case 'private':
      return intl.formatMessage(m.chipPrivate);
    case 'failed':
      return intl.formatMessage(m.chipFailed);
    case 'reconnect':
      return intl.formatMessage(m.chipReconnect);
    case 'removed':
      return intl.formatMessage(m.chipRemoved);
    case 'notMade':
      return intl.formatMessage(m.chipNotMade);
  }
}

/** "3:54" from seconds. */
export function videoLength(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

function readySentence(intl: IntlShape, item: YouTubeExtraItemView): string {
  const asset = item.readyAsset;
  if (asset === null) {
    return '';
  }
  if (asset.durationSec === null || asset.width === null || asset.height === null) {
    return intl.formatMessage(m.readyShort, { revision: asset.version });
  }
  return intl.formatMessage(m.ready, {
    revision: asset.version,
    length: videoLength(asset.durationSec),
    width: asset.width,
    height: asset.height,
  });
}

function renderSentence(intl: IntlShape, item: YouTubeExtraItemView): string {
  return item.part === null
    ? intl.formatMessage(m.making)
    : intl.formatMessage(m.makingPart, { current: item.part.current, total: item.part.total });
}

function failedSentence(intl: IntlShape, reason: string | null): string {
  if (reason === 'platformRefused') {
    return intl.formatMessage(m.failedRefused);
  }
  if (reason === 'fileGone' || reason === 'renderFailed') {
    return intl.formatMessage(m.failedFileGone);
  }
  return intl.formatMessage(m.failed);
}

/** The state sentence of one item. */
export function youtubeSentence(intl: IntlShape, item: YouTubeExtraItemView): string {
  const { record } = item;
  switch (item.state) {
    case 'notConnected':
      return intl.formatMessage(m.notConnected);
    case 'noVideo':
      return intl.formatMessage(m.noVideo, { credits: VIDEO_CREDIT_COST.fullSong });
    case 'making':
      return renderSentence(intl, item);
    case 'waiting':
      return `${renderSentence(intl, item)} ${intl.formatMessage(m.waiting)}`;
    case 'renderFailed':
      return intl.formatMessage(m.renderFailed);
    case 'ready':
      return readySentence(intl, item);
    case 'readyNotConnected':
      return intl.formatMessage(m.readyNotConnected);
    case 'uploading':
      return intl.formatMessage(m.uploading);
    case 'scheduled': {
      const at = record?.publishAt ?? null;
      return at === null
        ? intl.formatMessage(m.scheduledNoTime)
        : intl.formatMessage(m.scheduled, {
            date: intl.formatDate(at, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }),
            time: intl.formatTime(at, { hour: '2-digit', minute: '2-digit' }),
          });
    }
    case 'live':
      return record?.externalUrl === null || record?.externalUrl === undefined
        ? intl.formatMessage(m.chipOnYouTube)
        : intl.formatMessage(m.live, { link: shownLink(record.externalUrl) });
    case 'linked':
      return intl.formatMessage(m.linked);
    case 'failed':
      return failedSentence(intl, record?.failReason ?? null);
    case 'reconnect':
      return intl.formatMessage(m.reconnect);
    case 'removed':
      return record?.removedReason === 'private' ? intl.formatMessage(m.removedPrivate) : intl.formatMessage(m.removed);
  }
}

/** True when the sentence shows in the neg colour. */
export function youtubeSentenceIsBad(item: YouTubeExtraItemView): boolean {
  return (
    item.state === 'failed' || item.state === 'reconnect' || item.state === 'removed' || item.state === 'renderFailed'
  );
}
